import { RoleEnum } from '@zeus/index'
import { createBrowserRouter, redirect } from 'react-router-dom'
import { GQL_ME } from '~/gql/auth/auth'
import { client } from '~/helpers/apollo'
import { useAuth } from '~/helpers/store/auth'
import App from '~/pages/App'
import CreateAccount from '~/pages/authentication/CreateAccount'
import Denied from '~/pages/authentication/Denied'
import Login from '~/pages/authentication/Login'
import LoginSSO from '~/pages/authentication/LoginSSO'
import Logout from '~/pages/authentication/Logout'
import NewPassword from '~/pages/authentication/NewPassword'
import ResetPassword from '~/pages/authentication/ResetPassword'
import ValidAccount from '~/pages/authentication/ValidAccount'
import StatisticsTable from '~/pages/backoffice/admin/statistics/StatisticsTable'
import Error from '~/pages/errors/Error'
import NotFound from '~/pages/errors/NotFound'
import BackOfficeLayout from '~/pages/layouts/BackOfficeLayout'
import Root from '~/pages/layouts/Root'
import ScormPlayer from '~/pages/scorm/ScormPlayer'
import { trackNavigation } from '~/tracing'

export type UserData = {
  firstName: string
  lastName: string
  email: string
  role: RoleEnum
  id: string
} | null

export function getDefaultRoute(role: RoleEnum): string {
  switch (role) {
    case 'Admin' as RoleEnum:
      return '/back-office/statistics'
    case 'Formateur' as RoleEnum:
      return '/back-office/statistics'
    case 'User' as RoleEnum:
      return '/scorm'
    default:
      return '/'
  }
}

export function createProtectedLoader(allowedRoles: RoleEnum[]) {
  return async () => {
    if (allowedRoles.length === 0) {
      useAuth.getState().setLoading(false)
      return null
    }

    const currentState = useAuth.getState()

    if (currentState.payload) {
      const userRole = currentState.payload.role as RoleEnum

      if (!allowedRoles.includes(userRole)) {
        return redirect(getDefaultRoute(userRole))
      }

      return null
    }

    try {
      const { data } = await client.query({
        query: GQL_ME,
        fetchPolicy: 'network-only',
      })

      if (data?.me) {
        const userRole = data.me.roles as RoleEnum

        useAuth.getState().setPayload({
          firstName: data.me.firstName,
          lastName: data.me.lastName,
          email: data.me.email,
          role: userRole,
          id: data.me.id,
        })

        if (!allowedRoles.includes(userRole)) {
          return redirect(getDefaultRoute(userRole))
        }

        return null
      } else {
        useAuth.getState().clearAuth()
        return redirect(`/`)
      }
    } catch (_error) {
      useAuth.getState().clearAuth()
      return redirect(`/`)
    }
  }
}

export const router = createBrowserRouter([
  {
    path: '/',
    Component: Root,
    errorElement: <Error />,
    children: [
      {
        index: true,
        Component: App,
        loader: createProtectedLoader([]),
      },
      { path: 'logout', Component: Logout },
      { path: 'reset-password', Component: ResetPassword },
      { path: 'valid-account', Component: ValidAccount },
      { path: 'new-password', Component: NewPassword },
      { path: 'create-account', Component: CreateAccount },
      { path: 'sso', Component: LoginSSO },
      { path: 'denied', Component: Denied },
      { path: 'login', Component: Login },
      {
        path: 'scorm',
        Component: ScormPlayer,
        loader: createProtectedLoader([RoleEnum.Admin, RoleEnum.Formateur, RoleEnum.User]),
      },
      {
        path: 'back-office',
        Component: BackOfficeLayout,
        children: [
          {
            path: 'statistics',
            Component: StatisticsTable,
            loader: createProtectedLoader([RoleEnum.Admin]),
          },
        ],
      },
      { path: 'error', Component: Error, loader: createProtectedLoader([]) },
      { path: '*', Component: NotFound, loader: createProtectedLoader([]) },
    ],
  },
])

let lastTrackedKey = ''
router.subscribe((state) => {
  if (state.navigation.state !== 'idle') return
  const { pathname, search } = state.location
  const key = `${pathname}${search}`
  if (key === lastTrackedKey) return
  lastTrackedKey = key
  trackNavigation(pathname, search)
})
